import React, { useState, useEffect, useCallback } from 'react';
import OrganizationCard from './OrganizationCard';
import PublicOrganizationService from '../../services/publicOrganizationService';
import './OrganizationsDirectory.css';

const OrganizationsDirectory = () => {
  const [organizations, setOrganizations] = useState([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({});
  const [loadingMore, setLoadingMore] = useState(false);

  // Fetch organizations
  const fetchOrganizations = useCallback(async (lastKey = null, append = false) => {
    try {
      if (!append) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      const result = await PublicOrganizationService.getPublicOrganizations(50, lastKey);
      
      // Format organizations for display
      const formattedOrganizations = result.organizations.map(org => 
        PublicOrganizationService.formatOrganization(org)
      );

      if (append) {
        setOrganizations(prev => [...prev, ...formattedOrganizations]);
      } else {
        setOrganizations(formattedOrganizations);
      }

      setPagination(result.pagination);
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError('No se pudieron cargar las organizaciones. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Filter organizations based on search query
  useEffect(() => {
    const filtered = PublicOrganizationService.filterOrganizations(organizations, searchQuery);
    setFilteredOrganizations(filtered);
  }, [organizations, searchQuery]);

  // Handle search input
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle search clear
  const handleSearchClear = () => {
    setSearchQuery('');
  };

  // Handle load more
  const handleLoadMore = () => {
    if (pagination.hasMore && pagination.lastEvaluatedKey) {
      const lastKeyString = JSON.stringify(pagination.lastEvaluatedKey);
      fetchOrganizations(lastKeyString, true);
    }
  };

  // Handle retry
  const handleRetry = () => {
    fetchOrganizations();
  };

  if (loading && organizations.length === 0) {
    return (
      <div className="directory-container">
        <div className="directory-header">
          <div className="header-content">
            <h1 className="directory-title">Clínicas dentales</h1>
            <p className="directory-subtitle">
              Encuentra información de contacto de organizaciones dentales
            </p>
          </div>
        </div>

        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Cargando organizaciones...</p>
        </div>
      </div>
    );
  }

  if (error && organizations.length === 0) {
    return (
      <div className="directory-container">
        <div className="directory-header">
          <div className="header-content">
            <h1 className="directory-title">Clínicas dentales</h1>
            <p className="directory-subtitle">
              Encuentra información de contacto de organizaciones dentales
            </p>
          </div>
        </div>

        <div className="error-state">
          <div className="error-icon">
            <i className="fas fa-exclamation-circle"></i>
          </div>
          <h3>Error al cargar</h3>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={handleRetry}>
            <i className="fas fa-refresh"></i>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="directory-container">
      {/* Header */}
      <div className="directory-header">
        <div className="header-content">
          <h1 className="directory-title">Clínicas dentales</h1>
          <p className="directory-subtitle">
            Encuentra información de contacto de clínicas dentales cerca de ti
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <div className="search-container">
          <div className="search-input-wrapper">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              className="search-input"
              placeholder="Buscar por nombre, dirección, email o servicios..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            {searchQuery && (
              <button className="search-clear" onClick={handleSearchClear}>
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>

        {/* Search Results Info */}
        <div className="search-info">
          {searchQuery ? (
            <p>
              {filteredOrganizations.length} resultado{filteredOrganizations.length !== 1 ? 's' : ''} 
              {filteredOrganizations.length > 0 ? ` para "${searchQuery}"` : ` para "${searchQuery}"`}
            </p>
          ) : (
            <p>
              {organizations.length} clínica{organizations.length !== 1 ? 's' : ''} encontrada{organizations.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>

      {/* Organizations Grid */}
      <div className="organizations-grid">
        {filteredOrganizations.length > 0 ? (
          filteredOrganizations.map((organization) => (
            <OrganizationCard
              key={organization.id}
              organization={organization}
            />
          ))
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <i className="fas fa-search"></i>
            </div>
            <h3>No se encontraron resultados</h3>
            <p>
              {searchQuery 
                ? 'Intenta con diferentes términos de búsqueda'
                : 'No hay organizaciones disponibles en este momento'
              }
            </p>
            {searchQuery && (
              <button className="btn btn-outline" onClick={handleSearchClear}>
                Limpiar búsqueda
              </button>
            )}
          </div>
        )}
      </div>

      {/* Load More */}
      {!searchQuery && pagination.hasMore && (
        <div className="load-more-section">
          <button
            className="btn btn-outline load-more-btn"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? (
              <>
                <div className="loading-spinner small"></div>
                Cargando...
              </>
            ) : (
              <>
                <i className="fas fa-chevron-down"></i>
                Cargar más organizaciones
              </>
            )}
          </button>
        </div>
      )}

      {/* Footer */}
      <div className="directory-footer">
        <p>
          ¿Tu clínica no aparece en el directorio? 
          <a href="mailto:dentaris@digitaport.com" className="footer-link">
            Contáctanos
          </a>
        </p>
      </div>
    </div>
  );
};

export default OrganizationsDirectory;